import { useEffect } from 'react';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { ApiError, serverFieldErrors } from '@/api/errors';
import type { TKey } from '@/i18n/types';

import { scrollToFirstError } from './scroll-to-first-error';
import { validationMessage } from './validation';

const ZOD_CODE_TO_KEY = {
  too_small: 'validation.tooSmall',
  too_big: 'validation.tooBig',
  invalid_format: 'validation.invalidFormat',
  invalid_type: 'validation.invalidType',
  invalid_value: 'validation.mustAccept',
} as const;

/**
 * DE KODENE SOM IKKE KOMMER FRA ZOD, MEN FRA EN REGEL I PRODUKTET (QA runde 7, punkt 121).
 *
 * ------------------------------------------------------------------------------------------------
 * MÅLT I NETTLESEREN 14. SEPTEMBER, med forespørselen og svaret i hånda:
 *
 *   POST /v1/performance-guests → 400
 *   details: [{ path: 'seats', code: 'guest_seats_exceeded', message: '0 of 10 seats left.' }]
 *
 * På skjermen sto det «Verdien er ikke gyldig.» under et felt som inneholdt tallet 1. Rapporten
 * leste det som at skjemaet avviste seg selv — og det var en rimelig lesning, for produktet visste
 * nøyaktig hva som var galt og nektet å si det.
 *
 * Årsaken var dette oppslaget: alt som ikke er en zod-kode falt til `validation.custom`. Zod sine
 * fem koder beskriver FORMEN på en verdi; API-et har over hundre koder til som beskriver en REGEL,
 * og en regel har en setning som hjelper. «Verdien er ikke gyldig» har ingen.
 *
 * Tabellen er ikke komplett, og det er med vilje: de øvrige kodene lander i en `Callout` gjennom
 * `errorKey`, ikke på et felt. Den dagen en av dem gjør det, er raden her én linje — og til da er
 * `validation.custom` fortsatt et ærlig svar, bare et tynt et. Se `BACKLOG.md`.
 */
const API_CODE_TO_KEY = {
  /* 260.24: bare den som ikke er arrangøren, når taket (arrangøren går forbi det).
     Setningen ber derfor festivalen om et høyere tak. */
  guest_seats_exceeded: 'validation.api.guestSeatsExceededAskOrganizer',
} as const;

/**
 * Feltets egen setning for en kode i `details[].code` — én tabell, lest av skjemaene her og av
 * hurtigredigeringens celle (`components/ui/quick-edit`).
 */
export function fieldErrorKey(code: string): TKey {
  return (
    API_CODE_TO_KEY[code as keyof typeof API_CODE_TO_KEY] ??
    ZOD_CODE_TO_KEY[code as keyof typeof ZOD_CODE_TO_KEY] ??
    'validation.custom'
  );
}

/**
 * Server-side validation failures land on the field that caused them, derived from
 * `details[].path` (§12.3). The API's English `message` is never used; only its stable code.
 */
export function useApiFormErrors<TValues extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<TValues>,
): void {
  useEffect(() => {
    if (!(error instanceof ApiError)) return;
    /* `body.email` fra pipa er `email` i skjemaet (A8): uten prefikset traff ingen feil feltet. */
    for (const [path, code] of Object.entries(serverFieldErrors(error))) {
      setError(path as Path<TValues>, {
        type: 'server',
        message: validationMessage(fieldErrorKey(code)),
      });
    }

    /*
     * OG SÅ DIT (QA13 runde 3, blokk 3 — rest 31a).
     *
     * -------------------------------------------------------------------------------------------
     * En feil satt på et felt under skjermkanten er en beskjed som teknisk sett er gitt og
     * praktisk talt ikke er det. Leseren trykker på knappen en gang til, fordi ingenting synlig
     * skjedde — og den andre forespørselen får det samme avslaget.
     *
     * HER OG IKKE I HVERT SKJEMA, av samme grunn som feilkodene oversettes her: dette er ett
     * svar på ett spørsmål — «hva skjer når serveren avviser et felt» — og det skal finnes ett
     * sted. Hvert skjema som bruker denne kroken får oppførselen uten å vite om den.
     *
     * `requestAnimationFrame` fordi `setError` over er en tilstandsendring: feltet bærer ikke
     * `data-field-invalid` før React har tegnet, og å lete i samme sving ville funnet forrige
     * rendrings feil — eller ingen.
     */
    const frame = requestAnimationFrame(() => scrollToFirstError());
    return () => cancelAnimationFrame(frame);
  }, [error, setError]);
}
